/**
 * 경량 자체 구문강조 토크나이저 (A1, 무의존).
 *
 * 외부 하이라이터(Shiki/highlight.js) 없이 정규식 1패스로 토큰을 분류해
 * <span class="tok-*">로 감싼 HTML 문자열을 만든다. 정확도는 제한적(키워드 집합 기반,
 * 문맥 무시)이지만 의존성 0을 유지한다.
 *
 * 모드: "markup"(html/xml/svg) · "code"(그 외, C형 + hash 주석 옵션).
 * 언어 미지정("" 또는 "text")은 강조하지 않는다(임의 텍스트 오색 방지).
 *
 * 보안: 토큰 텍스트는 항상 HTML 이스케이프 후 span으로 감싼다(코드 내용에 의한 마크업 주입 불가).
 */

function esc(s) {
  return String(s == null ? "" : s)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
}

// 공통(C형) 키워드 — JS/TS/Java/C/C++/C#/Go/Rust 등 합집합(문맥 무시, lossy)
const KW_C = (
  "const let var function return if else for while do switch case break continue " +
  "new class extends super this typeof instanceof in of try catch finally throw " +
  "async await yield import export from default delete void static get set " +
  "interface type enum implements public private protected readonly namespace declare as keyof infer " +
  "int long short char float double bool boolean string void struct union template typename " +
  "package func go defer chan map range select fn let mut impl trait pub use mod match where unsafe"
).split(/\s+/);

const KW_PY = (
  "def class return if elif else for while import from as pass break continue lambda yield " +
  "with try except finally raise global nonlocal assert del is not and or in async await"
).split(/\s+/);

const KW_SQL = (
  "select from where insert into update delete create table drop alter add column join left right " +
  "inner outer full on group by order having limit offset as and or not null values set distinct union all " +
  "primary key foreign references default index view trigger procedure function begin end"
).split(/\s+/);

const KW_SH = (
  "if then fi else elif for do done while until case esac function in select return local export readonly " +
  "echo cd exit set unset source"
).split(/\s+/);

const BOOLS = new Set(["true", "false", "null", "undefined", "None", "True", "False", "nil", "NaN", "void"]);

// 언어 → { kw, mode, hash, ci(대소문자무시 키워드) }
const LANGS = {
  javascript: { kw: KW_C, mode: "code" },
  js: { kw: KW_C, mode: "code" },
  typescript: { kw: KW_C, mode: "code" },
  ts: { kw: KW_C, mode: "code" },
  jsx: { kw: KW_C, mode: "code" },
  tsx: { kw: KW_C, mode: "code" },
  json: { kw: [], mode: "code" },
  java: { kw: KW_C, mode: "code" },
  c: { kw: KW_C, mode: "code" },
  cpp: { kw: KW_C, mode: "code" },
  csharp: { kw: KW_C, mode: "code" },
  go: { kw: KW_C, mode: "code" },
  rust: { kw: KW_C, mode: "code" },
  php: { kw: KW_C, mode: "code", hash: true },
  python: { kw: KW_PY, mode: "code", hash: true },
  ruby: { kw: KW_PY, mode: "code", hash: true },
  bash: { kw: KW_SH, mode: "code", hash: true },
  shell: { kw: KW_SH, mode: "code", hash: true },
  sh: { kw: KW_SH, mode: "code", hash: true },
  yaml: { kw: [], mode: "code", hash: true },
  toml: { kw: [], mode: "code", hash: true },
  sql: { kw: KW_SQL, mode: "code", ci: true },
  css: { kw: [], mode: "code" },
  scss: { kw: [], mode: "code" },
  html: { kw: [], mode: "markup" },
  xml: { kw: [], mode: "markup" },
  svg: { kw: [], mode: "markup" },
  markdown: { kw: [], mode: "code" },
  text: null,
  "": null,
};

/** 지원 언어(드롭다운 표시용) — value/label */
export const CODE_LANGUAGES = [
  { value: "", label: "텍스트" },
  { value: "javascript", label: "JavaScript" },
  { value: "typescript", label: "TypeScript" },
  { value: "jsx", label: "JSX" },
  { value: "tsx", label: "TSX" },
  { value: "json", label: "JSON" },
  { value: "html", label: "HTML" },
  { value: "xml", label: "XML" },
  { value: "css", label: "CSS" },
  { value: "python", label: "Python" },
  { value: "java", label: "Java" },
  { value: "c", label: "C" },
  { value: "cpp", label: "C++" },
  { value: "csharp", label: "C#" },
  { value: "go", label: "Go" },
  { value: "rust", label: "Rust" },
  { value: "php", label: "PHP" },
  { value: "ruby", label: "Ruby" },
  { value: "sql", label: "SQL" },
  { value: "bash", label: "Bash" },
  { value: "yaml", label: "YAML" },
  { value: "markdown", label: "Markdown" },
];

function span(cls, text) {
  return `<span class="tok-${cls}">${esc(text)}</span>`;
}

function highlightCode(code, cfg) {
  const kwSet = new Set(cfg.ci ? cfg.kw.map((k) => k.toLowerCase()) : cfg.kw);
  const comment = cfg.hash ? "(\\/\\/[^\\n]*|\\/\\*[\\s\\S]*?\\*\\/|#[^\\n]*)" : "(\\/\\/[^\\n]*|\\/\\*[\\s\\S]*?\\*\\/)";
  const re = new RegExp(
    comment + // 1 comment
      "|(\"(?:[^\"\\\\]|\\\\.)*\"|'(?:[^'\\\\]|\\\\.)*'|`(?:[^`\\\\]|\\\\.)*`)" + // 2 string
      "|(\\b\\d[\\w.]*\\b)" + // 3 number
      "|([A-Za-z_$][\\w$]*)", // 4 word
    "g"
  );
  let out = "";
  let last = 0;
  let m;
  while ((m = re.exec(code))) {
    if (m.index > last) out += esc(code.slice(last, m.index));
    if (m[1] != null) out += span("com", m[1]);
    else if (m[2] != null) out += span("str", m[2]);
    else if (m[3] != null) out += span("num", m[3]);
    else if (m[4] != null) {
      const w = m[4];
      const key = cfg.ci ? w.toLowerCase() : w;
      if (BOOLS.has(w)) out += span("bool", w);
      else if (kwSet.has(key)) out += span("kw", w);
      else out += esc(w);
    }
    last = re.lastIndex;
  }
  if (last < code.length) out += esc(code.slice(last));
  return out;
}

function highlightMarkup(code) {
  const re = /(<!--[\s\S]*?-->)|(<\/?[A-Za-z][\w:-]*)|("(?:[^"\\]|\\.)*"|'(?:[^'\\]|\\.)*')|([A-Za-z_:][\w:-]*)(?=\s*=)|(\/?>)/g;
  let out = "";
  let last = 0;
  let m;
  while ((m = re.exec(code))) {
    if (m.index > last) out += esc(code.slice(last, m.index));
    if (m[1] != null) out += span("com", m[1]);
    else if (m[2] != null) out += span("kw", m[2]);
    else if (m[3] != null) out += span("str", m[3]);
    else if (m[4] != null) out += span("attr", m[4]);
    else if (m[5] != null) out += span("kw", m[5]);
    last = re.lastIndex;
  }
  if (last < code.length) out += esc(code.slice(last));
  return out;
}

/**
 * 코드 문자열 → 강조 HTML. lang 미지원/미지정이면 이스케이프만(강조 없음).
 * 줄바꿈("\n")은 보존(codeBlock .content는 white-space:pre-wrap).
 */
export function highlightToHtml(code, lang) {
  const cfg = LANGS[String(lang || "").toLowerCase()];
  if (!cfg) return esc(code); // 미지정/미지원 → 강조 안 함(안전)
  return cfg.mode === "markup" ? highlightMarkup(code) : highlightCode(code, cfg);
}
